import { PermissionEn } from '@martech-solution/utils/constants';
import type { RouteGroup } from '@martech-solution/utils/types';
import { lazy } from 'react';
import urlPath from 'src/constants/urlPath';
import MainLayout from 'src/layouts/MainLayout';

// Lazy load Ticket master data components
const TicketCategoryGroup = lazy(() => import('src/pages/Ticket/TicketCategoryGroup'));
const TicketCategory = lazy(() => import('src/pages/Ticket/TicketCategory'));
const AddTicketCategory = lazy(() => import('src/pages/Ticket/TicketCategory/AddTicketCategory'));
const TicketCategoryDetail = lazy(() => import('src/pages/Ticket/TicketCategoryDetail'));
const TicketType = lazy(() => import('src/pages/Ticket/TicketType'));
const TicketForm = lazy(() => import('src/pages/Ticket/TicketForm/TicketForm'));
const AddTicketForm = lazy(() => import('src/pages/Ticket/TicketForm/AddTicketForm'));
const TicketFormDetail = lazy(() => import('src/pages/Ticket/TicketFormDetail'));
const Reason = lazy(() => import('src/pages/Ticket/Reason'));
const TicketFormMapping = lazy(() => import('src/pages/Ticket/TicketFormMapping'));

// Lazy load master data components
const Branch = lazy(() => import('src/pages/MasterData/Branch'));
const Department = lazy(() => import('src/pages/MasterData/Department'));
const Employee = lazy(() => import('src/pages/MasterData/Employee'));
const SeasonalEmployee = lazy(() => import('src/pages/MasterData/SeasonalEmployee'));
const Position = lazy(() => import('src/pages/MasterData/Position'));
const AddEmployee = lazy(() => import('src/pages/MasterData/AddEmployee'));
const AddSeasonalEmployee = lazy(() => import('src/pages/MasterData/AddSeasonalEmployee'));
const EmployeeQrCode = lazy(() => import('src/pages/MasterData/EmployeeQrCode'));
const SeasonalEmployeeQrCode = lazy(() => import('src/pages/MasterData/SeasonalEmployeeQrCode'));
const EmployeeDetail = lazy(() => import('src/pages/MasterData/EmployeeDetail'));
const SeasonalEmployeeDetail = lazy(() => import('src/pages/MasterData/SeasonalEmployeeDetail'));
const Board = lazy(() => import('src/pages/MasterData/Board'));
const Level = lazy(() => import('src/pages/MasterData/Level'));
const Tier = lazy(() => import('src/pages/MasterData/Tier'));
const JobCategory = lazy(() => import('src/pages/MasterData/JobCategory'));
const AddJobCategory = lazy(() => import('src/pages/MasterData/AddJobCategory'));
const OrganizationChart = lazy(() => import('src/pages/MasterData/OrganizationChart'));
const AddOrganizationChart = lazy(() => import('src/pages/MasterData/OrganizationChart/AddOrganizationChart'));
const OrganizationChartDetail = lazy(() => import('src/pages/MasterData/OrganizationChart/OrganizationChartDetail'));
const Job = lazy(() => import('src/pages/MasterData/Job'));
const Duty = lazy(() => import('src/pages/MasterData/Duty'));
const JobDescription = lazy(() => import('src/pages/MasterData/JobDescription'));
const Asset = lazy(() => import('src/pages/MasterData/Asset'));
const AssetType = lazy(() => import('src/pages/MasterData/AssetType'));
const AssetGroup = lazy(() => import('src/pages/MasterData/AssetGroup'));
const DegreeType = lazy(() => import('src/pages/MasterData/DegreeType'));
const Degree = lazy(() => import('src/pages/MasterData/Degree'));
const PartnerType = lazy(() => import('src/pages/MasterData/PartnerType'));
const Partner = lazy(() => import('src/pages/MasterData/Partner'));
const AddPartner = lazy(() => import('src/pages/MasterData/AddPartner'));
const AccountingAccountType = lazy(() => import('src/pages/MasterData/AccountingAccountType'));
const GeneralAccountingAccount = lazy(() => import('src/pages/MasterData/GeneralAccountingAccount'));
const ContractType = lazy(() => import('src/pages/MasterData/ContractType'));
const ContractTemplate = lazy(() => import('src/pages/MasterData/ContractTemplate'));
const ApplicationInfo = lazy(() => import('src/pages/MasterData/ApplicationInfo'));
const Certificate = lazy(() => import('src/pages/MasterData/Certificate'));
const ErrorCode = lazy(() => import('src/pages/MasterData/ErrorCode'));
const HRTicketCategory = lazy(() => import('src/pages/MasterData/HRTicketCategory'));
const Resource = lazy(() => import('src/pages/MasterData/Resource'));
const Role = lazy(() => import('src/pages/MasterData/Role'));
const UserResource = lazy(() => import('src/pages/MasterData/UserResource'));
const AddRole = lazy(() => import('src/pages/MasterData/AddRole'));
const Permission = lazy(() => import('src/pages/MasterData/Permission'));
const Holiday = lazy(() => import('src/pages/MasterData/Holiday'));
const SundayCompensatory = lazy(() => import('src/pages/MasterData/SundayCompensatory'));
const FuelConsumption = lazy(() => import('src/pages/MasterData/FuelConsumption'));
const FuelUnitPrice = lazy(() => import('src/pages/MasterData/FuelUnitPrice'));
const VehicleType = lazy(() => import('src/pages/MasterData/VehicleType'));
const SubSystem = lazy(() => import('src/pages/MasterData/SubSystem'));
const WorkDescription = lazy(() => import('src/pages/MasterData/WorkDescription'));
const AddSubSystem = lazy(() => import('src/pages/MasterData/AddSubSystem'));
const AddApplicationInfo = lazy(() => import('src/pages/MasterData/AddApplicationInfo'));
const ApplicationInfoDetail = lazy(() => import('src/pages/MasterData/ApplicationInfoDetail'));
const Distance = lazy(() => import('src/pages/MasterData/Distance'));
const UserTicketCategory = lazy(() => import('src/pages/MasterData/UserTicketCategory'));
const UserJobCategory = lazy(() => import('src/pages/MasterData/UserJobCategory'));
const Shift = lazy(() => import('src/pages/MasterData/Shift'));
const ManagerAssignment = lazy(() => import('src/pages/MasterData/ManagerAssignment'));
const MobileVersion = lazy(() => import('src/pages/MasterData/MobileVersion'));
const ProductionUnit = lazy(() => import('src/pages/MasterData/ProductionUnit'));
const AddProductionUnit = lazy(() => import('src/pages/MasterData/AddProductionUnit'));
const AcceptancePermission = lazy(() => import('src/pages/MasterData/AcceptancePermission'));

const DataTable = lazy(() => import('src/pages/MasterData/DataTable'));
const AddDataTable = lazy(() => import('src/pages/MasterData/DataTable/AddDataTable'));

const SiteLocation = lazy(() => import('src/pages/MasterData/SiteLocation'));
const Cost = lazy(() => import('src/pages/MasterData/Cost'));

const BusinessProcessCatalog = lazy(() => import('src/pages/MasterData/BusinessProcessCatalog'));
const ObjType = lazy(() => import('src/pages/MasterData/ObjType'));
const ObjGroup = lazy(() => import('src/pages/MasterData/ObjGroup'));
const IssueCategory = lazy(() => import('src/pages/MasterData/IssueCategory'));
const Obj = lazy(() => import('src/pages/MasterData/Obj'));
const IssueCause = lazy(() => import('src/pages/MasterData/IssueCause'));
const IssueSolution = lazy(() => import('src/pages/MasterData/IssueSolution'));
const User = lazy(() => import('src/pages/MasterData/User'));

// Sử dụng bên Sản xuất (PM)
const Factory = lazy(() => import('src/pages/MasterData/Factory'));
const ProductionLine = lazy(() => import('src/pages/MasterData/ProductionLine'));
const ProposalType = lazy(() => import('src/pages/MasterData/ProposalType/index'));
const WareHouse = lazy(() => import('src/pages/MasterData/WareHouse'));

export const masterDataRoutes: RouteGroup = [
  // Core Master Data
  {
    path: urlPath.branch.location,
    Layout: MainLayout,
    Component: Branch,
    checkPath: urlPath.branch.location
  },
  {
    path: urlPath.department.location,
    Layout: MainLayout,
    Component: Department,
    checkPath: urlPath.department.location
  },

  // Employee Management
  {
    path: urlPath.employee.location,
    Layout: MainLayout,
    Component: Employee,
    checkPath: urlPath.employee.location
  },
  {
    path: urlPath.employeeAdd.location,
    Layout: MainLayout,
    Component: AddEmployee,
    permission: PermissionEn.create
  },
  {
    path: urlPath.employeeQR.location,
    Layout: MainLayout,
    Component: EmployeeQrCode,
    permission: PermissionEn.get_qr
  },
  {
    path: `${urlPath.employeeEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddEmployee,
    permission: PermissionEn.update
  },
  {
    path: `${urlPath.employeeDetail.location}/:id`,
    Layout: MainLayout,
    Component: EmployeeDetail,
    permission: PermissionEn.viewDetail
  },

  // Seasonal Employee Management
  {
    path: urlPath.seasonalEmployee.location,
    Layout: MainLayout,
    Component: SeasonalEmployee,
    checkPath: urlPath.seasonalEmployee.location
  },
  {
    path: urlPath.seasonalEmployeeAdd.location,
    Layout: MainLayout,
    Component: AddSeasonalEmployee,
    permission: PermissionEn.create
  },
  {
    path: urlPath.seasonalEmployeeQR.location,
    Layout: MainLayout,
    Component: SeasonalEmployeeQrCode,
    permission: PermissionEn.get_qr
  },
  {
    path: `${urlPath.seasonalEmployeeEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddSeasonalEmployee,
    permission: PermissionEn.update
  },
  {
    path: `${urlPath.seasonalEmployeeDetail.location}/:id`,
    Layout: MainLayout,
    Component: SeasonalEmployeeDetail,
    permission: PermissionEn.viewDetail
  },

  // Organizational Structure
  {
    path: urlPath.position.location,
    Layout: MainLayout,
    Component: Position,
    checkPath: urlPath.position.location
  },
  {
    path: urlPath.board.location,
    Layout: MainLayout,
    Component: Board,
    checkPath: urlPath.board.location
  },
  {
    path: urlPath.level.location,
    Layout: MainLayout,
    Component: Level,
    checkPath: urlPath.level.location
  },
  {
    path: urlPath.tier.location,
    Layout: MainLayout,
    Component: Tier,
    checkPath: urlPath.tier.location
  },
  {
    path: urlPath.jobCategory.location,
    Layout: MainLayout,
    Component: JobCategory,
    checkPath: urlPath.jobCategory.location
  },
  {
    path: urlPath.jobCategoryAdd.location,
    Layout: MainLayout,
    Component: AddJobCategory,
    permission: PermissionEn.create
  },
  {
    path: `${urlPath.jobCategoryEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddJobCategory,
    permission: PermissionEn.update
  },
  {
    path: urlPath.organizationChart.location,
    Layout: MainLayout,
    Component: OrganizationChart,
    checkPath: urlPath.organizationChart.location
  },
  {
    path: urlPath.organizationChartAdd.location,
    Layout: MainLayout,
    Component: AddOrganizationChart,
    checkPath: urlPath.organizationChart.location
  },
  {
    path: `${urlPath.organizationChartEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddOrganizationChart,
    checkPath: urlPath.organizationChart.location
  },
  {
    path: `${urlPath.organizationChart.location}/:id`,
    Layout: MainLayout,
    Component: OrganizationChartDetail,
    checkPath: urlPath.organizationChart.location
  },
  {
    path: urlPath.job.location,
    Layout: MainLayout,
    Component: Job,
    checkPath: urlPath.job.location
  },
  {
    path: urlPath.duty.location,
    Layout: MainLayout,
    Component: Duty,
    checkPath: urlPath.duty.location
  },
  {
    path: urlPath.jobDescription.location,
    Layout: MainLayout,
    Component: JobDescription,
    checkPath: urlPath.jobDescription.location
  },

  // Assets and Resources
  {
    path: urlPath.assetGroup.location,
    Layout: MainLayout,
    Component: AssetGroup,
    checkPath: urlPath.assetGroup.location
  },
  {
    path: urlPath.assetType.location,
    Layout: MainLayout,
    Component: AssetType,
    checkPath: urlPath.assetType.location
  },
  {
    path: urlPath.asset.location,
    Layout: MainLayout,
    Component: Asset,
    checkPath: urlPath.asset.location
  },
  {
    path: urlPath.degreeType.location,
    Layout: MainLayout,
    Component: DegreeType,
    checkPath: urlPath.degreeType.location
  },
  {
    path: urlPath.degree.location,
    Layout: MainLayout,
    Component: Degree,
    checkPath: urlPath.degree.location
  },
  {
    path: urlPath.certificate.location,
    Layout: MainLayout,
    Component: Certificate,
    checkPath: urlPath.certificate.location
  },

  // Partners
  {
    path: urlPath.partnerType.location,
    Layout: MainLayout,
    Component: PartnerType,
    checkPath: urlPath.partnerType.location
  },
  {
    path: urlPath.partner.location,
    Layout: MainLayout,
    Component: Partner,
    checkPath: urlPath.partner.location
  },
  {
    path: `${urlPath.partnerEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddPartner,
    permission: PermissionEn.update
  },
  {
    path: urlPath.partnerAdd.location,
    Layout: MainLayout,
    Component: AddPartner,
    permission: PermissionEn.create
  },

  // Accounting
  {
    path: urlPath.accountingAccountType.location,
    Layout: MainLayout,
    Component: AccountingAccountType,
    checkPath: urlPath.accountingAccountType.location
  },
  {
    path: urlPath.generalAccountingAccount.location,
    Layout: MainLayout,
    Component: GeneralAccountingAccount,
    checkPath: urlPath.generalAccountingAccount.location
  },
  {
    path: urlPath.contractType.location,
    Layout: MainLayout,
    Component: ContractType,
    checkPath: urlPath.contractType.location
  },
  {
    path: urlPath.contractTemplate.location,
    Layout: MainLayout,
    Component: ContractTemplate,
    checkPath: urlPath.contractTemplate.location
  },

  // System Configuration
  {
    path: urlPath.applicationInfo.location,
    Layout: MainLayout,
    Component: ApplicationInfo,
    checkPath: urlPath.applicationInfo.location
  },
  {
    path: `${urlPath.applicationInfoEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddApplicationInfo,
    permission: PermissionEn.update
  },
  {
    path: urlPath.applicationInfoAdd.location,
    Layout: MainLayout,
    Component: AddApplicationInfo,
    permission: PermissionEn.create
  },
  {
    path: `${urlPath.applicationInfoDetail.location}/:id`,
    Layout: MainLayout,
    Component: ApplicationInfoDetail,
    permission: PermissionEn.viewDetail
  },
  {
    path: urlPath.errorCode.location,
    Layout: MainLayout,
    Component: ErrorCode,
    checkPath: urlPath.errorCode.location
  },
  {
    path: urlPath.hrTicketCategory.location,
    Layout: MainLayout,
    Component: HRTicketCategory,
    checkPath: urlPath.hrTicketCategory.location
  },

  // Security and Permissions
  {
    path: urlPath.resource.location,
    Layout: MainLayout,
    Component: Resource,
    checkPath: urlPath.resource.location
  },
  {
    path: urlPath.role.location,
    Layout: MainLayout,
    Component: Role,
    checkPath: urlPath.role.location
  },
  {
    path: urlPath.roleAdd.location,
    Layout: MainLayout,
    Component: AddRole,
    permission: PermissionEn.create
  },
  {
    path: `${urlPath.roleEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddRole,
    permission: PermissionEn.update
  },
  {
    path: urlPath.userResource.location,
    Layout: MainLayout,
    Component: UserResource,
    checkPath: urlPath.userResource.location
  },
  {
    path: urlPath.permission.location,
    Layout: MainLayout,
    Component: Permission,
    checkPath: urlPath.permission.location
  },

  // Operational Data
  {
    path: urlPath.distance.location,
    Layout: MainLayout,
    Component: Distance,
    checkPath: urlPath.distance.location
  },
  {
    path: urlPath.holiday.location,
    Layout: MainLayout,
    Component: Holiday,
    checkPath: urlPath.holiday.location
  },
  {
    path: urlPath.sundayCompensatory.location,
    Layout: MainLayout,
    Component: SundayCompensatory,
    checkPath: urlPath.sundayCompensatory.location
  },
  {
    path: urlPath.fuelConsumption.location,
    Layout: MainLayout,
    Component: FuelConsumption,
    checkPath: urlPath.fuelConsumption.location
  },
  {
    path: urlPath.fuelUnitPrice.location,
    Layout: MainLayout,
    Component: FuelUnitPrice,
    checkPath: urlPath.fuelUnitPrice.location
  },
  {
    path: urlPath.vehicleType.location,
    Layout: MainLayout,
    Component: VehicleType,
    checkPath: urlPath.vehicleType.location
  },
  {
    path: urlPath.subSystem.location,
    Layout: MainLayout,
    Component: SubSystem,
    checkPath: urlPath.subSystem.location
  },
  {
    path: urlPath.subSystemAdd.location,
    Layout: MainLayout,
    Component: AddSubSystem,
    checkPath: urlPath.subSystem.location
  },
  {
    path: `${urlPath.subSystemEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddSubSystem,
    checkPath: urlPath.subSystem.location
  },
  {
    path: urlPath.workDescription.location,
    Layout: MainLayout,
    Component: WorkDescription,
    checkPath: urlPath.workDescription.location
  },

  // User Categories
  {
    path: urlPath.userTicketCategory.location,
    Layout: MainLayout,
    Component: UserTicketCategory,
    checkPath: urlPath.userTicketCategory.location
  },
  {
    path: urlPath.userJobCategory.location,
    Layout: MainLayout,
    Component: UserJobCategory,
    checkPath: urlPath.userJobCategory.location
  },

  // Shift and Warehouse Management
  {
    path: urlPath.shift.location,
    Layout: MainLayout,
    Component: Shift,
    checkPath: urlPath.shift.location
  },
  {
    path: urlPath.managerAssignment.location,
    Layout: MainLayout,
    Component: ManagerAssignment,
    checkPath: urlPath.managerAssignment.location
  },
  {
    path: urlPath.wareHouse.location,
    Layout: MainLayout,
    Component: WareHouse,
    checkPath: urlPath.wareHouse.location
  },
  // Production and Operations
  {
    path: urlPath.mobileVersion.location,
    Layout: MainLayout,
    Component: MobileVersion,
    checkPath: urlPath.mobileVersion.location
  },
  {
    path: urlPath.productionUnit.location,
    Layout: MainLayout,
    Component: ProductionUnit,
    checkPath: urlPath.productionUnit.location
  },
  {
    path: urlPath.proposalType.location,
    Layout: MainLayout,
    Component: ProposalType,
    checkPath: urlPath.proposalType.location
  },
  {
    path: urlPath.productionUnitAdd.location,
    Layout: MainLayout,
    Component: AddProductionUnit,
    checkPath: urlPath.productionUnit.location
  },
  {
    path: `${urlPath.productionUnitEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddProductionUnit,
    checkPath: urlPath.productionUnit.location
  },
  {
    path: urlPath.acceptancePermission.location,
    Layout: MainLayout,
    Component: AcceptancePermission,
    checkPath: urlPath.acceptancePermission.location
  },
  {
    path: urlPath.dataTable.location,
    Layout: MainLayout,
    Component: DataTable,
    checkPath: urlPath.dataTable.location
  },
  {
    path: urlPath.dataTableAdd.location,
    Layout: MainLayout,
    Component: AddDataTable,
    checkPath: urlPath.dataTable.location
  },
  {
    path: `${urlPath.dataTableEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddDataTable,
    checkPath: urlPath.dataTable.location
  },
  {
    path: urlPath.siteLocation.location,
    Layout: MainLayout,
    Component: SiteLocation,
    checkPath: urlPath.siteLocation.location
  },
  {
    path: urlPath.cost.location,
    Layout: MainLayout,
    Component: Cost,
    checkPath: urlPath.cost.location
  },

  // TICKET MASTER DATA
  {
    path: urlPath.ticketType.location,
    Layout: MainLayout,
    Component: TicketType,
    checkPath: urlPath.ticketType.location
  },
  {
    path: urlPath.ticketCategoryGroup.location,
    Layout: MainLayout,
    Component: TicketCategoryGroup,
    checkPath: urlPath.ticketCategoryGroup.location
  },
  {
    path: urlPath.ticketCategory.location,
    Layout: MainLayout,
    Component: TicketCategory,
    checkPath: urlPath.ticketCategory.location
  },
  {
    path: urlPath.ticketCategoryAdd.location,
    Layout: MainLayout,
    Component: AddTicketCategory,
    checkPath: urlPath.ticketCategory.location
  },
  {
    path: `${urlPath.ticketCategoryEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddTicketCategory,
    checkPath: urlPath.ticketCategory.location
  },
  {
    path: `${urlPath.ticketCategoryDetail.location}/:id`,
    Layout: MainLayout,
    Component: TicketCategoryDetail,
    permission: PermissionEn.viewDetail
  },
  {
    path: urlPath.ticketForm.location,
    Layout: MainLayout,
    Component: TicketForm,
    checkPath: urlPath.ticketForm.location
  },
  {
    path: `${urlPath.ticketFormDetail.location}/:id`,
    Layout: MainLayout,
    Component: TicketFormDetail,
    permission: PermissionEn.viewDetail
  },
  {
    path: urlPath.ticketFormAdd.location,
    Layout: MainLayout,
    Component: AddTicketForm,
    permission: PermissionEn.create
  },
  {
    path: `${urlPath.ticketFormEdit.location}/:id`,
    Layout: MainLayout,
    Component: AddTicketForm,
    permission: PermissionEn.update
  },
  {
    path: urlPath.reason.location,
    Layout: MainLayout,
    Component: Reason,
    checkPath: urlPath.reason.location
  },
  {
    path: urlPath.ticketFormMapping.location,
    Layout: MainLayout,
    Component: TicketFormMapping,
    checkPath: urlPath.ticketFormMapping.location
  },
  {
    path: urlPath.businessProcessCatalog.location,
    Layout: MainLayout,
    Component: BusinessProcessCatalog,
    checkPath: urlPath.businessProcessCatalog.location
  },
  {
    path: urlPath.objType.location,
    Layout: MainLayout,
    Component: ObjType,
    checkPath: urlPath.objType.location
  },
  {
    path: urlPath.objGroup.location,
    Layout: MainLayout,
    Component: ObjGroup,
    checkPath: urlPath.objGroup.location
  },
  {
    path: urlPath.obj.location,
    Layout: MainLayout,
    Component: Obj,
    checkPath: urlPath.obj.location
  },
  {
    path: urlPath.issueCategory.location,
    Layout: MainLayout,
    Component: IssueCategory,
    checkPath: urlPath.issueCategory.location
  },
  {
    path: urlPath.issueCause.location,
    Layout: MainLayout,
    Component: IssueCause,
    checkPath: urlPath.issueCause.location
  },
  {
    path: urlPath.issueSolution.location,
    Layout: MainLayout,
    Component: IssueSolution,
    checkPath: urlPath.issueSolution.location
  },
  {
    path: urlPath.user.location,
    Layout: MainLayout,
    Component: User,
    checkPath: urlPath.user.location
  },

  // Sử dụng bên sản xuất (PM)
  {
    path: urlPath.factory.location,
    Layout: MainLayout,
    Component: Factory,
    checkPath: urlPath.factory.location
  },
  {
    path: urlPath.productionLine.location,
    Layout: MainLayout,
    Component: ProductionLine,
    checkPath: urlPath.productionLine.location
  },
  {
    path: urlPath.proposalType.location,
    Layout: MainLayout,
    Component: ProposalType,
    checkPath: urlPath.proposalType.location
  }
  // {
  //   path: urlPath.productionLineAdd.location,
  //   Layout: MainLayout,
  //   Component: ProductionLineAdd,
  //   checkPath: urlPath.productionLineAdd.location
  // },
  // {
  //   path: `${urlPath.productionLineEdit.location}/:id`,
  //   Layout: MainLayout,
  //   Component: ProductionLineAdd,
  //   checkPath: urlPath.productionLineAdd.location
  // }
];
